import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./people.css";
import { getComplianceOverview, listTechnicianProfiles, listEmployees, techUserId } from "../../api/teamApi";

/* Team → Compliance: what the business must hold (requirement pack), who holds
   what — one row per credential, sortable — and what's expiring. A holder opens
   the card on its Compliance tab, where the license PDF is uploaded and read. */

const fmtDate = (v) => (v ? new Date(v + (String(v).length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const humanize = (v) => String(v || "—").replace(/_/g, " ");
const STATUS_RANK = { expired: 0, missing: 0, revoked: 0, expiring: 1, expiring_soon: 1, pending: 2, active: 3, valid: 3, current: 3 };

const COLS = [
  { key: "holder", label: "Holder" },
  { key: "label", label: "Credential" },
  { key: "number", label: "Number" },
  { key: "expiry_date", label: "Expires" },
  { key: "status", label: "Status" },
];

export default function TeamCompliance({ openEmployee }) {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [techs, setTechs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "status", dir: "asc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, t, e] = await Promise.all([getComplianceOverview(), listTechnicianProfiles().catch(() => ({ data: [] })), listEmployees().catch(() => ({ data: {} }))]);
      setOv(o.data || {}); setTechs(Array.isArray(t.data) ? t.data : t.data?.results || []); setEmployees(e.data?.employees || []);
    } catch { toast.error("Couldn’t load compliance."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const techToUser = useMemo(() => new Map(techs.map((t) => [Number(t.id), techUserId(t)])), [techs]);
  const nameByUser = useMemo(() => new Map(employees.map((e) => [e.user_id, e.name])), [employees]);
  const holderOf = useCallback((c) => {
    const uid = techToUser.get(Number(c.holder_tech_id));
    return { uid, name: (uid && nameByUser.get(uid)) || c.holder_name || (c.is_company ? "Company" : c.holder_tech_id ? `Tech #${c.holder_tech_id}` : "—") };
  }, [techToUser, nameByUser]);

  // overview.requirements is a block: { requirements: [...], needs_jurisdiction, jurisdiction, industry }
  const reqBlock = ov?.requirements || {};
  const requirements = Array.isArray(reqBlock) ? reqBlock : (reqBlock.requirements || []);
  const needsState = !!reqBlock.needs_jurisdiction;
  const credentials = useMemo(() => ov?.credentials || [], [ov]);
  const risks = useMemo(() => ov?.risks || [], [ov]);
  const flags = useMemo(() => ov?.flags || [], [ov]);

  const rows = useMemo(() => {
    const list = credentials.map((c) => { const h = holderOf(c); return { ...c, holder: h.name, holder_uid: h.uid, label: c.label || humanize(c.type_key) }; });
    const { key, dir } = sort; const m = dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (key === "status") return ((STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9)) * m || String(a.holder).localeCompare(String(b.holder));
      if (key === "expiry_date") return (String(a.expiry_date || "9999") > String(b.expiry_date || "9999") ? 1 : -1) * m;
      return String(a[key] || "").localeCompare(String(b[key] || "")) * m;
    });
    return list;
  }, [credentials, holderOf, sort]);
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  // flags come one per person with no holder on the row: fold identical flags into one line, naming holders when the row carries one
  const flagRows = useMemo(() => {
    const groups = new Map();
    flags.forEach((f) => {
      const label = f.label || f.title || humanize(f.code);
      const detail = f.detail || f.message || "";
      const key = `${label}|${detail}`;
      const holder = f.holder_tech_id != null ? holderOf(f).name : f.holder_name || "";
      const g = groups.get(key) || { key, label, detail, count: 0, holders: [] };
      g.count += 1;
      if (holder && !g.holders.includes(holder)) g.holders.push(holder);
      groups.set(key, g);
    });
    return [...groups.values()];
  }, [flags, holderOf]);
  // expiry risks: one row per credential, holder named
  const riskRows = useMemo(() => risks.map((r, i) => ({ id: `r${i}`, holder: r.holder_tech_id != null ? holderOf(r).name : r.holder_name || "", label: r.label || humanize(r.type_key), detail: r.detail || r.message || (r.expiry_date ? `expires ${fmtDate(r.expiry_date)}` : ""), pill: humanize(r.severity || r.status || "risk") })), [risks, holderOf]);

  return (
    <div className="team-tab">
      <div className="ppl-grid">
        <section className="ppl-card">
          <div className="ppl-row-head">
            <h3>What the business must hold{requirements.length ? <span className="ppl-count">{requirements.length}</span> : null}</h3>
            <button type="button" className="ppl-link" onClick={() => navigate("/settings/compliance")}>Settings → Compliance</button>
          </div>
          {loading ? <div className="ppl-muted">Loading…</div>
            : requirements.length === 0 ? <div className="ppl-muted">{needsState ? "Set the state under Settings → Compliance to get the requirement pack." : "No requirement pack for this tenant’s industry and state yet."}</div>
            : <div className="ppl-list">{requirements.map((r, i) => (
              <div key={r.type_key || i} className="ppl-list-row">
                <div className="ppl-list-main"><span className="ppl-name">{r.label || humanize(r.type_key)}</span><span className="ppl-sub">{r.scope || r.kind || ""}{r.issuing_body ? ` · ${r.issuing_body}` : ""}</span></div>
                <span className={`ppl-pill ${r.status === "met" || r.satisfied ? "approved" : r.status === "expiring" ? "pending" : "danger"}`}>{r.status ? humanize(r.status) : r.satisfied ? "held" : "missing"}</span>
              </div>
            ))}</div>}
        </section>

        <section className="ppl-card">
          <h3>Expiry risk{riskRows.length + flagRows.length ? <span className="ppl-count">{riskRows.length + flagRows.length}</span> : null}</h3>
          {loading ? <div className="ppl-muted">Loading…</div>
            : riskRows.length === 0 && flagRows.length === 0 ? <div className="ppl-muted">Nothing expiring or flagged.</div>
            : <div className="ppl-list">
              {riskRows.map((r) => <div key={r.id} className="ppl-list-row"><div className="ppl-list-main"><span className="ppl-name">{r.holder ? `${r.holder} · ` : ""}{r.label}</span><span className="ppl-sub">{r.detail}</span></div><span className="ppl-pill pending">{r.pill}</span></div>)}
              {flagRows.map((f) => <div key={f.key} className="ppl-list-row"><div className="ppl-list-main"><span className="ppl-name">{f.holders.length ? `${f.holders.join(", ")} · ` : ""}{f.label}</span><span className="ppl-sub">{f.detail}{f.count > 1 && !f.holders.length ? ` · ${f.count} people` : ""}</span></div><span className="ppl-pill">{f.count > 1 ? `${f.count} flags` : "flag"}</span></div>)}
            </div>}
        </section>

        <section className="ppl-card ppl-card--wide">
          <div className="ppl-row-head">
            <h3>Who holds what{rows.length ? <span className="ppl-count">{rows.length}</span> : null}</h3>
            <span className="ppl-muted">A row opens the holder’s card. Add a license from its PDF there.</span>
          </div>
          {loading ? <div className="ppl-muted">Loading…</div>
            : rows.length === 0 ? <div className="ppl-empty"><strong>No credentials on file.</strong><span>Open a person’s card → Compliance and upload the license PDF.</span></div>
            : (
              <div className="ppl-table-wrap">
                <table className="ppl-table">
                  <thead><tr>
                    {COLS.map((c) => (
                      <th key={c.key} className={sort.key === c.key ? "is-sorted" : ""}>
                        <button type="button" onClick={() => toggleSort(c.key)}>{c.label}{sort.key === c.key && <span className="sort-dir">{sort.dir === "asc" ? "▲" : "▼"}</span>}</button>
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr key={c.id}>
                        <td className="nowrap">{c.holder_uid ? <button type="button" className="ppl-link ppl-name" onClick={() => openEmployee(c.holder_uid, "compliance")}>{c.holder}</button> : <span className="ppl-name">{c.holder}</span>}</td>
                        <td>{c.label}{c.issuing_body ? <span className="ppl-sub">{c.issuing_body}</span> : null}</td>
                        <td className="muted nowrap">{c.number || "—"}</td>
                        <td className="muted nowrap">{c.expiry_date ? fmtDate(c.expiry_date) : "no expiry on file"}</td>
                        <td><span className={`ppl-pill ${c.status}`}>{humanize(c.status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </section>
      </div>
    </div>
  );
}
